import React, { useMemo } from "react";
import BudgetProgressBar from "./BudgetProgressBar";
import { useTranslation } from "react-i18next";

const CartSidebar = ({
  cart,
  budget,
  lang,
  limitWidth = true,
  showBudget = true,
}) => {
  const { t } = useTranslation();

  const isPendingItem = (it) =>
    it?.isWaitingForContact === true ||
    it?.proposeDates === true ||
    it?.isPending === true;

  const payableItems = useMemo(
    () => cart.filter((it) => !isPendingItem(it)),
    [cart]
  );
  const pendingItems = useMemo(
    () => cart.filter((it) => isPendingItem(it)),
    [cart]
  );

  const formatMoney = (value) => `$${Number(value || 0).toFixed(2)}`;

  const getOptionLabel = (optionKey) => {
    const key = String(optionKey || "").toLowerCase();
    if (key === "color") return t("cartSidebar.option.color");
    if (key === "material") return t("cartSidebar.option.material");
    return optionKey;
  };

  const lineTotal = (item) =>
    item.totalPrice ?? Number(item.realPrice || 0) * Number(item.quantity || 1);

  const totalAll = useMemo(
    () => cart.reduce((sum, it) => sum + lineTotal(it), 0),
    [cart]
  );
  const totalPayable = useMemo(
    () => payableItems.reduce((sum, it) => sum + lineTotal(it), 0),
    [payableItems]
  );

  const getOptionsObject = (item) => {
    if (item?.selectedOptions && typeof item.selectedOptions === "object")
      return item.selectedOptions;
    if (item?.options && typeof item.options === "object") return item.options;
    if (item?.optionsJson) {
      try {
        const o = JSON.parse(item.optionsJson);
        if (o && typeof o === "object") return o;
      } catch {}
    }
    return null;
  };

  const SectionList = ({ items, pending = false }) => {
    if (!items?.length) return null;
    return (
      <ul className="space-y-6">
        {items.map((item, index) => (
          <li key={index} className="border-b border-gray-300 pb-2">
            <div className="flex justify-between items-start gap-3">
              {/* 5) image */}
              {item.imageUrl && (
                <img
                  src={item.imageUrl}
                  alt=""
                  className="w-12 h-12 object-cover rounded-md flex-shrink-0 hidden"
                />
              )}

              <div className="flex flex-col flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  {/* 3) title + fallback */}
                  <span className="font-semibold text-base truncate">
                    {item.title ??
                      (lang === "fr" ? item.nameFr : item.nameEn) ??
                      ""}
                  </span>
                  {pending && (
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-300">
                      {t("cartSidebar.pending", "À confirmer")}
                    </span>
                  )}
                </div>

                {/* 3) description */}
                {item.description && (
                  <p className="text-xs text-gray-600 mt-0.5 line-clamp-2">
                    {item.description}
                  </p>
                )}

                {/* 4) optionName (camel + fallback) */}
                {(item.optionName ?? item.OptionName) && (
                  <span className="text-sm text-gray-500 mt-1">
                    {item.optionName ?? item.OptionName}
                  </span>
                )}

                {/* 4) options détaillées */}
                {(() => {
                  const opts = getOptionsObject(item);
                  return opts && Object.keys(opts).length > 0 ? (
                    <div className="mt-1 space-y-0.5">
                      {Object.entries(opts).map(([k, v]) => (
                        <div key={k} className="text-xs text-gray-500">
                          <span className="font-medium">
                            {getOptionLabel(k)}:
                          </span>{" "}
                          <span>{String(v)}</span>
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>

              <div className="text-right flex-shrink-0">
                <span className="text-sm text-gray-700">x{item.quantity}</span>
                <br />
                <span className="text-sm font-medium text-gray-800">
                  {formatMoney(lineTotal(item))}
                </span>
              </div>
            </div>
          </li>
        ))}
      </ul>
    );
  };

  return (
    <div
      className={`bg-gray-150 p-6 h-screen sticky top-0 overflow-y-auto rounded-lg text-primary ${
        limitWidth ? "max-w-1/4" : "w-full"
      }`}
    >
      {showBudget && <BudgetProgressBar budget={budget} cart={cart} />}

      <h3 className="text-xl font-bold mb-4">{t("cartSidebar.title")}</h3>

      {cart.length === 0 ? (
        <p className="text-gray-500">{t("cartSidebar.empty")}</p>
      ) : (
        <>
          {payableItems.length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-gray-700 mb-2">
                {t("cartSidebar.pay_now", "À payer maintenant")}
              </h4>
              <SectionList items={payableItems} />
            </>
          )}

          {pendingItems.length > 0 && (
            <>
              <h4 className="text-sm font-semibold text-gray-700 mt-6 mb-2">
                {t("cartSidebar.pending_title", "En attente (à confirmer)")}
              </h4>
              <SectionList items={pendingItems} pending />
              <p className="text-xs text-gray-600 mt-2">
                {t(
                  "cartSidebar.pending_hint",
                  "Les articles à confirmer ne seront pas facturés maintenant. Le fournisseur vous contactera pour proposer une date."
                )}
              </p>
            </>
          )}
        </>
      )}

      {cart.length > 0 && (
        <div className="text-right font-semibold mt-6 space-y-1">
          <div>
            {t("cartSidebar.subtotal_payable", "Sous-total payable")}:{" "}
            {formatMoney(totalPayable)}
          </div>
          <div>
            {t("cartSidebar.total", "Total")}: {formatMoney(totalAll)}
          </div>
        </div>
      )}
    </div>
  );
};

export default CartSidebar;
